import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { runs as runsApi } from "@/api/endpoints";
import { serverDetail } from "@/api/client";
import StatusPill from "@/components/StatusPill";
import SpinnerButton from "@/components/SpinnerButton";
import ConfirmDialog from "@/components/ConfirmDialog";
import KpiTile from "@/components/KpiTile";
import EmptyState from "@/components/EmptyState";
import OpenInWorkbenchButton from "@/components/OpenInWorkbenchButton";
import { SkeletonTableRows } from "@/components/Skeleton";
import { fmtDuration, relativeTime, shortId } from "@/lib/format";

const STATUSES = ["", "queued", "running", "succeeded", "failed", "cancelled"];
const RUN_TYPES = ["", "monte_carlo", "sweep", "guidance_compare"];

function isWithin24h(iso) {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && Date.now() - t <= 24 * 60 * 60 * 1000;
}

export default function RunsListPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [allItems, setAllItems] = useState([]); // for KPI strip (last 500)
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [runType, setRunType] = useState("");
    const [limit, setLimit] = useState(50);
    const [skip, setSkip] = useState(0);
    const [confirm, setConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const [page, all] = await Promise.all([
                runsApi.list({
                    q: q || undefined,
                    status: status || undefined,
                    run_type: runType || undefined,
                    limit,
                    skip,
                }),
                runsApi.list({ limit: 500, skip: 0 }),
            ]);
            setItems(page);
            setAllItems(all);
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setLoading(false);
        }
    }, [q, status, runType, limit, skip]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const hasInflight = useMemo(
        () => items.some((r) => r.status === "queued" || r.status === "running"),
        [items],
    );

    useEffect(() => {
        if (!hasInflight) return undefined;
        const id = setInterval(refresh, 5000);
        return () => clearInterval(id);
    }, [hasInflight, refresh]);

    const kpis = useMemo(() => {
        const last24 = allItems.filter((r) => isWithin24h(r.submitted_at));
        return {
            runs24: last24.length,
            ok24: last24.filter((r) => r.status === "succeeded").length,
            fail24: last24.filter((r) => r.status === "failed" || r.status === "cancelled").length,
            running: allItems.filter((r) => r.status === "running" || r.status === "queued").length,
        };
    }, [allItems]);

    function askDelete(r) {
        setConfirm(r);
    }

    async function doDelete() {
        if (!confirm) return;
        setDeleting(true);
        try {
            await runsApi.remove(confirm.id);
            setConfirm(null);
            await refresh();
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-[20px] font-semibold text-ux-text-1" data-testid="runs-list-title">
                    Runs
                </h1>
                <div className="flex items-center gap-2">
                    <SpinnerButton
                        variant="secondary"
                        loading={loading}
                        onClick={refresh}
                        testId="runs-list-refresh"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </SpinnerButton>
                    <SpinnerButton onClick={() => navigate("/plan/scenarios")} testId="runs-list-new-run-button">
                        <Plus className="w-4 h-4" /> New Scenario
                    </SpinnerButton>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiTile label="Runs · 24h" value={kpis.runs24} testId="runs-kpi-24h" />
                <KpiTile label="Succeeded · 24h" value={kpis.ok24} testId="runs-kpi-ok-24h" />
                <KpiTile label="Failed · 24h" value={kpis.fail24} testId="runs-kpi-fail-24h" />
                <KpiTile label="Running now" value={kpis.running} testId="runs-kpi-running" />
            </div>

            <div className="bg-ux-bg-1 border border-ux-border-1 rounded-md p-3 grid grid-cols-1 md:grid-cols-5 gap-2">
                <input
                    type="text"
                    placeholder="Search name…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="text-[13px] bg-ux-bg-3 border border-ux-border-1 rounded-md px-2 h-9 text-ux-text-1 focus:outline-none focus:border-ux-accent"
                    data-testid="runs-list-search"
                />
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="text-[13px] bg-ux-bg-3 border border-ux-border-1 rounded-md px-2 h-9 text-ux-text-1 focus:outline-none focus:border-ux-accent"
                    data-testid="runs-list-status-filter"
                >
                    {STATUSES.map((s) => (
                        <option key={s} value={s}>
                            {s ? `status: ${s}` : "all statuses"}
                        </option>
                    ))}
                </select>
                <select
                    value={runType}
                    onChange={(e) => setRunType(e.target.value)}
                    className="text-[13px] bg-ux-bg-3 border border-ux-border-1 rounded-md px-2 h-9 text-ux-text-1 focus:outline-none focus:border-ux-accent"
                    data-testid="runs-list-runtype-filter"
                >
                    {RUN_TYPES.map((s) => (
                        <option key={s} value={s}>
                            {s ? s : "all run types"}
                        </option>
                    ))}
                </select>
                <select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className="text-[13px] bg-ux-bg-3 border border-ux-border-1 rounded-md px-2 h-9 text-ux-text-1 focus:outline-none focus:border-ux-accent"
                    data-testid="runs-list-limit"
                >
                    {[25, 50, 100].map((n) => (
                        <option key={n} value={n}>
                            {n} per page
                        </option>
                    ))}
                </select>
                <div className="flex gap-1">
                    <SpinnerButton
                        variant="secondary"
                        disabled={skip === 0}
                        onClick={() => setSkip(Math.max(0, skip - limit))}
                        testId="runs-list-prev-page"
                        className="flex-1"
                    >
                        Prev
                    </SpinnerButton>
                    <SpinnerButton
                        variant="secondary"
                        onClick={() => setSkip(skip + limit)}
                        disabled={items.length < limit}
                        testId="runs-list-next-page"
                        className="flex-1"
                    >
                        Next
                    </SpinnerButton>
                </div>
            </div>

            {err && (
                <div className="text-[13px] text-ux-text-1 bg-ux-danger-soft border border-ux-danger/40 rounded-md p-2">
                    {err}
                </div>
            )}

            <div className="overflow-x-auto bg-ux-bg-1 border border-ux-border-1 rounded-md">
                <table className="min-w-[640px] w-full text-[13px]" data-testid="runs-list-table">
                    <thead className="text-ux-text-3 sticky top-0 bg-ux-bg-1">
                        <tr className="border-b border-ux-border-2">
                            <th className="text-left px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Status</th>
                            <th className="text-left px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Name</th>
                            <th className="text-left px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Type</th>
                            <th className="text-right px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Submitted</th>
                            <th className="hidden md:table-cell text-right px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Started</th>
                            <th className="text-right px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Duration</th>
                            <th className="hidden lg:table-cell text-right px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Rows</th>
                            <th className="hidden md:table-cell text-left px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Source</th>
                            <th className="hidden lg:table-cell text-left px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Error</th>
                            <th className="text-right px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && items.length === 0 && <SkeletonTableRows rows={5} cols={10} />}
                        {!loading && items.length === 0 && (
                            <tr>
                                <td colSpan={10} className="px-0 py-0">
                                    <EmptyState
                                        testId="runs-empty"
                                        title="No runs match these filters."
                                        subtitle="Open a scenario from the Scenarios library and press Run in the header to submit a Monte Carlo."
                                        actionLabel="Open Scenarios"
                                        onAction={() => navigate("/plan/scenarios")}
                                        actionTestId="runs-empty-cta"
                                        className="rounded-none border-0"
                                    />
                                </td>
                            </tr>
                        )}
                        {items.map((r, idx) => (
                            <tr
                                key={r.id}
                                onClick={() => navigate(`/runs/${r.id}`)}
                                className={`border-t border-ux-border-1 hover:bg-ux-bg-2 cursor-pointer h-12 ${
                                    idx % 2 === 0 ? "bg-ux-bg-1" : "bg-[color-mix(in_oklab,var(--ux-bg-1),var(--ux-bg-0))]"
                                }`}
                                data-testid={`runs-list-row-${r.id}`}
                            >
                                <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                                <td className="px-3 py-2">
                                    <Link
                                        to={`/runs/${r.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-ux-text-1 hover:text-ux-accent font-medium"
                                    >
                                        {r.name}
                                    </Link>
                                    <div className="text-[11px] text-ux-text-3 font-mono">{shortId(r.id)}</div>
                                </td>
                                <td className="px-3 py-2 font-mono text-[12px] text-ux-text-2">{r.run_type}</td>
                                <td className="px-3 py-2 text-right text-ux-text-2 tabular-nums">{relativeTime(r.submitted_at)}</td>
                                <td className="hidden md:table-cell px-3 py-2 text-right text-ux-text-2 tabular-nums">{relativeTime(r.started_at)}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtDuration(r.duration_s)}</td>
                                <td className="hidden lg:table-cell px-3 py-2 text-right tabular-nums font-mono">
                                    {r.sweep_rows ?? r.guidance_rows ?? "—"}
                                </td>
                                <td className="hidden md:table-cell px-3 py-2 text-[12px] text-ux-text-3">{r.source_mode}</td>
                                <td className="hidden lg:table-cell px-3 py-2">
                                    {r.error_type ? (
                                        <span className="text-[11px] text-ux-danger font-mono">{r.error_type}</span>
                                    ) : (
                                        <span className="text-ux-text-3">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                    <OpenInWorkbenchButton
                                        seedName={r.name}
                                        description={`Seeded from run ${r.id}`}
                                        variant="ghost"
                                        className="mr-1"
                                        testId={`runs-list-open-workbench-${r.id}`}
                                    >
                                        Workbench
                                    </OpenInWorkbenchButton>
                                    <button
                                        type="button"
                                        disabled={!["succeeded", "failed", "cancelled"].includes(r.status)}
                                        onClick={() => askDelete(r)}
                                        className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-md text-ux-danger hover:bg-ux-danger-soft disabled:opacity-30 disabled:cursor-not-allowed"
                                        data-testid={`runs-list-delete-${r.id}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                    </button>
                                    {/* keep view test-id for compatibility */}
                                    <Link
                                        to={`/runs/${r.id}`}
                                        data-testid={`runs-list-view-${r.id}`}
                                        className="hidden"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmDialog
                open={!!confirm}
                title="Delete run?"
                message={
                    confirm
                        ? `This will permanently delete run "${confirm.name}" and all its artefacts.`
                        : ""
                }
                confirmLabel="Delete"
                loading={deleting}
                onCancel={() => setConfirm(null)}
                onConfirm={doDelete}
            />
        </div>
    );
}
